import { CachedWeatherData, WeatherData } from './types';

const WEATHER_CACHE_KEY = 'gfs_weather_cache';
const DEFAULT_TTL = 20 * 60 * 1000; // 20 minutes in milliseconds

/**
 * Check if localStorage is available (client-side only)
 */
function isLocalStorageAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const testKey = '__storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get cached weather data if valid
 * Returns null if cache is expired or unavailable
 */
export function getCachedWeather(): WeatherData | null {
  if (!isLocalStorageAvailable()) return null;

  try {
    const cached = window.localStorage.getItem(WEATHER_CACHE_KEY);
    if (!cached) return null;

    const parsedCache: CachedWeatherData = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is expired
    if (now >= parsedCache.expiresAt) {
      window.localStorage.removeItem(WEATHER_CACHE_KEY);
      return null;
    }

    return parsedCache.data;
  } catch (error) {
    console.warn('Error reading weather cache:', error);
    return null;
  }
}

/**
 * Save weather data to cache with TTL
 */
export function setCachedWeather(
  data: WeatherData,
  ttl: number = DEFAULT_TTL
): void {
  if (!isLocalStorageAvailable()) return;

  try {
    const now = Date.now();
    const cacheEntry: CachedWeatherData = {
      data,
      timestamp: now,
      expiresAt: now + ttl,
    };

    window.localStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify(cacheEntry));
  } catch (error) {
    console.warn('Error saving weather cache:', error);
  }
}

/**
 * Clear weather cache
 */
export function clearWeatherCache(): void {
  if (!isLocalStorageAvailable()) return;

  try {
    window.localStorage.removeItem(WEATHER_CACHE_KEY);
  } catch (error) {
    console.warn('Error clearing weather cache:', error);
  }
}
